import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import RemediationTriggerList from "./RemediationTriggerList";
import { ArrowLeft, DollarSign, Download, Plus, FileText, Shield, Building, ChevronDown, ChevronRight, Search } from "lucide-react";
import Pagination from "../components/Pagination";
import { usePageSize } from "../hooks/usePageSize";
import { useRegions } from "../contexts/RegionContext";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import Navigation from "../components/Navigation";
import { PINNED_PANEL_TOP } from "../components/PinnedNavigation";
import LoadingSpinner from "../components/LoadingSpinner";
import Card from '../components/Card';
import Button from '../components/Buttons';
import Input from '../components/Input';

import AssignRiskModal from "../components/AssignRiskModal";
import BulkAssignRiskModal from "../components/BulkAssignRiskModal";
import FinancialImpactSection from '../components/FinancialImpactSection';
import FairFactorTree from '../components/FairFactorTree';
import { isFairAligned } from '../utils/financialEngineUtil';
import FairMethodologyDisclaimer from './shared/FairMethodologyDisclaimer';
import {
  RISK_STATUS_FILTER_DEFAULT, applyRiskStatusFilter, countActiveRisks, isActiveRisk,
  RiskStatusFilter, RiskStatusBadge, RiskSourcesFilter,
} from './shared/riskStatus';
import AssessmentContextHeader from './shared/AssessmentContextHeader';

import ReactSpeedometer from "react-d3-speedometer";
import FrameworkComplianceBars from "../components/FrameworkComplianceBars";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import ActivityLogTab from './ActivityLogTab';
import { useShowFrameworkRiskExposure } from '../contexts/FeatureFlagsContext';
import { computeOrganizationAnalytics } from '../utils/riskScoringEngine';
import { OverallRiskScoreCard, ControlEffectivenessCard, ResidualRiskCard, ComplianceStatusCard, VulnerabilityDistributionCard, PredictiveRiskBars, RiskSourceBreakdownCard } from './RiskScoreCards';
import { parseRemediationSteps } from '../utils/remediationSteps';

// Assessment Risks View Component - Shows risks and remediation controls for specific assessment
// A single mapped value that's a scoping note rather than a real control code (e.g. "N/A - out of
// scope for the US Bulk Electric System / NERC CIP assessment") — never rendered as a control chip,
// even when it rides alongside real control codes in the same framework's mapping.
const isNotApplicableEntry = (c) => typeof c === "string" && /^\s*n\/a\b/i.test(c);

const isFrameworkNotApplicable = (controls) => {
  // frameworkImpact values arrive as string[] (see backend/Extensions/FrameworkImpactConverter.cs —
  // Dictionary<string, List<string>>, even for a single value), so a plain typeof === "string"
  // check never matches and every N/A note slips through as a 1-element array.
  const list = Array.isArray(controls) ? controls : typeof controls === "string" ? [controls] : [];
  return list.length > 0 && list.every(isNotApplicableEntry);
};

// Framework Group card, e.g. SOC2_Type_II_TSC, NIST_CSF_2_0, and others. Hoisted to module scope
// (was previously defined inside AssessmentRisksView's render body) because it now holds its own
// `expanded` state — a component redefined inline on every parent render gets a new identity each
// time, so React remounts it and any local state resets right after the parent's next re-render.
const FrameworkCard = ({ title, controls = "", color }) => {
  const [expanded, setExpanded] = useState(false);
  const bgColors = {
    blue: "bg-blue-50 border-blue-300",
    green: "bg-green-50 border-green-300",
    purple: "bg-purple-50 border-purple-300",
    orange: "bg-orange-50 border-orange-300",
    red: "bg-red-50 border-red-300",
    pink: "bg-pink-50 border-pink-300",
    indigo: "bg-indigo-50 border-indigo-300",
    yellow: "bg-yellow-50 border-yellow-300",
    teal: "bg-teal-50 border-teal-300",
    cyan: "bg-cyan-50 border-cyan-300",
    gray: "bg-gray-100 border-gray-300",
    emerald: "bg-emerald-50 border-emerald-300",
    violet: "bg-violet-50 border-violet-300",
    rose: "bg-rose-50 border-rose-300",
    amber: "bg-amber-50 border-amber-300",
    sky: "bg-sky-50 border-sky-300",
  }

  const controlList = (Array.isArray(controls)
    ? controls
    : typeof controls === "string"
      ? controls.split(";").map(c => c.trim()).filter(Boolean)
      : []
  ).filter(c => !isNotApplicableEntry(c))

  const visibleControls = expanded ? controlList : controlList.slice(0, 2)
  const hiddenCount = controlList.length - visibleControls.length

  return (
    <div className={`min-w-[85px] px-2 py-2 rounded-md border ${bgColors[color] || "bg-gray-50 border-gray-300"}`}>
      <div className="text-[9px] font-semibold text-center uppercase leading-tight">
        {title}
      </div>

      <div className="flex flex-wrap gap-1 justify-center mt-1">
        {visibleControls.map((c, i) => (
          <span key={i} className="px-1 py-[1px] bg-white border rounded text-[8px]">
            {c}
          </span>
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-[8px] text-gray-400 hover:text-gray-700 hover:underline cursor-pointer"
            title={`Show ${hiddenCount} more control${hiddenCount === 1 ? "" : "s"}`}
          >
            +{hiddenCount}
          </button>
        )}
        {expanded && controlList.length > 2 && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-[8px] text-gray-400 hover:text-gray-700 hover:underline cursor-pointer"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  )
}

const AssessmentRisksView = () => {
  const [assessment, setAssessment] = useState(null);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showMonetaryAnalysis, setShowMonetaryAnalysis] = useState(false);
  //const [selectedRiskId, setSelectedRiskId] = useState(null);

  const [frameworkScores, setFrameworkScores] = useState(null);
  const showFrameworkRiskExposure = useShowFrameworkRiskExposure();

  const [organizationAnalytics, setOrganizationAnalytics] = useState(null);
  const [quantificationFactors, setQuantificationFactors] = useState(null);
  const [verticalId, setVerticalId] = useState(null);

  const [sortBy, setSortBy] = useState('severity');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize('assessmentRisks');
  const listTopRef = useRef(null);

  const [step, setStep] = useState("setup");
  const [analysis, setAnalysis] = useState(null);
  const [showEngineBreakdown, setShowEngineBreakdown] = useState(false);
  const [activeCostCategory, setActiveCostCategory] = useState(null);
  // Expandable scenario-bands panel — keyed by risk.id
  const [openScenarioBands, setOpenScenarioBands] = useState({});
  const toggleScenarioBands = (riskId) =>
    setOpenScenarioBands(prev => ({ ...prev, [riskId]: !prev[riskId] }));
  // Active tab per risk card — keyed by risk.id
  const [riskActiveTabs, setRiskActiveTabs] = useState({});
  const [expandedCards, setExpandedCards] = useState({});
  const toggleCard = (id) => setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  const [parameters, setParameters] = useState({
    companySize: "medium",
    annualRevenue: "10-50M",
    criticalSystems: "moderate",
    dataClassification: "confidential",
    regulatoryRequirements: "standard",
  });

  const [complianceData, setComplianceData] = useState({
    compliance_status: {},
    overall_compliance: 0,
  });
  const [loadingCompliance, setLoadingCompliance] = useState(false);
  const [zeroTrustScores, setZeroTrustScores] = useState({});
  const [zeroTrustOverall, setZeroTrustOverall] = useState(0);
  const [loadingZeroTrust, setLoadingZeroTrust] = useState(false);

  const [predictiveRisk, setPredictiveRisk] = useState({
    day45: 0,
    day90: 0,
    day180: 0,
  });
  const [loadingPredictive, setLoadingPredictive] = useState(false);

  const [riskCategories, setRiskCategories] = useState({});
  const [loadingCategories, setLoadingCategories] = useState(false);

  const apiCall = useApi();
  const { canManageOrganizationRisks, canManageDeal, canManageBusinessUnit } = useRBAC();
  const selectedOrg = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const regions = useRegions();
  const orgName = selectedOrg?.organizationName;
  const contextRegionId = assessment?.regionId ?? selectedOrg?.regionId;
  const regionName = contextRegionId
    ? regions?.find(r => r.regionId === contextRegionId)?.regionName
    : null;
  const [dealName, setDealName] = useState(null);
  const [businessUnitName, setBusinessUnitName] = useState(null);

  // Fetch deal name and business unit name once the assessment is loaded
  useEffect(() => {
    if (!assessment) return;
    // AIAssessmentDto serializes DealID as "DealId" ([JsonPropertyName]), bypassing camelCase.
    const dealId = assessment.DealId || assessment.dealID || assessment.dealId;
    const buId = assessment.businessUnitId;
    if (dealId) {
      apiCall('GET', `/deals/${dealId}`)
        .then(d => setDealName(d?.dealName || null))
        .catch(() => {});
    }
    if (buId) {
      apiCall('GET', `/businessUnits/${buId}`)
        .then(b => setBusinessUnitName(b?.businessUnitName || null))
        .catch(() => {});
    }
  }, [assessment?.aiAssessmentID]);
  const canManageOrgRisks = canManageOrganizationRisks();
  const canManageRisks = canManageOrgRisks
      || ((assessment?.DealId || assessment?.dealID) && canManageDeal(assessment.DealId || assessment.dealID))
      || (assessment?.businessUnitId && canManageBusinessUnit(assessment.businessUnitId));

  const [selectedRiskIds, setSelectedRiskIds]         = useState(new Set());
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);

  const toggleRiskSelection = (riskId) =>
    setSelectedRiskIds(prev => {
      const next = new Set(prev);
      next.has(riskId) ? next.delete(riskId) : next.add(riskId);
      return next;
    });

  const toggleSelectAll = () =>
    setSelectedRiskIds(prev =>
      prev.size === filteredRisks.length && filteredRisks.length > 0
        ? new Set()
        : new Set(filteredRisks.map(r => r.id))
    );

  const generateRecommendations = (totalImpact, riskCount) => {
    const recommendations = [];
    if (totalImpact > 1000000) {
      recommendations.push(
        "Immediate executive attention required - potential impact exceeds $1M"
      );
      recommendations.push("Consider cyber insurance coverage review");
    }
    if (riskCount > 20) {
      recommendations.push("Implement risk management framework");
      recommendations.push("Prioritize critical and high-risk remediation");
    }
    recommendations.push("Regular security assessments recommended");
    recommendations.push("Consider third-party security audit");
    return recommendations;
  };

  // Compact currency formatter: always M with 3 decimal places (e.g. $0.462M, $1.851M)
  const formatImpact = (val) => {
    if (val == null) return '—';
    return `$${(Number(val) / 1_000_000).toFixed(3)}M`;
  };

  const calculateMonetaryImpact = async () => {
    setLoading(true);
    setStep("analyzing");

    try {
      // Use risks already loaded in state — they contain engine-calculated financial fields
      // from the backend MonetaryImpactEngine (finalCalculatedMonetaryImpact, multipliers, etc.)
      // Re-fetch only if state is empty (edge case)
      const allRisks = risks.length > 0
        ? risks
        : await apiCall("GET", `/aiAssessments/${assessment.aiAssessmentID}/risks`);
      // Exclude remediated risks from financial impact analysis — only active risks contribute
      const risksToAnalyze = allRisks.filter(r => !r.isRemediationCompleted).length > 0
        ? allRisks.filter(r => !r.isRemediationCompleted)
        : allRisks;

      let totalEngineImpact = 0;    // sum of Expected (Most Likely) — used for aggregation
      let totalEngineImpactLow = 0; // sum of Low scenario
      let totalEngineImpactHigh = 0;// sum of High scenario
      let engineRiskCount = 0;
      const engineByRiskType = {}; // { "DB": { count, subtotal, subtotalLow, subtotalHigh }, ... }

      const breakdown = {
        direct_costs: 0,
        business_interruption: 0,
        regulatory_fines: 0,
        reputation_damage: 0,
        recovery_costs: 0,
      };

      risksToAnalyze.forEach((risk) => {
        let riskImpact = 0;

        if (risk.impliedExpectedAnnualContribution != null || risk.finalCalculatedMonetaryImpact != null) {
          // v3.0: use probability-weighted, deduplicated portfolio contribution per finding.
          // v2.0 fallback: use raw SLE when contribution fields are not yet populated.
          // SUM(impliedExpectedAnnualContribution) == portfolio totalExpectedAnnualLoss exactly.
          riskImpact = Number(risk.impliedExpectedAnnualContribution ?? risk.finalCalculatedMonetaryImpact);
          totalEngineImpact += riskImpact;
          // v3.1 scenario bands — use probability-weighted EAL bands (same full pipeline as Expected).
          // Fall back to Expected when EAL bands not yet populated (pre-v3.1 or legacy records).
          // DO NOT use impactLow/impactHigh here — those are raw SLEs, not probability-weighted.
          totalEngineImpactLow += Number(risk.impliedExpectedAnnualContributionLow ?? risk.impliedExpectedAnnualContribution ?? risk.finalCalculatedMonetaryImpact ?? 0);
          totalEngineImpactHigh += Number(risk.impliedExpectedAnnualContributionHigh ?? risk.impliedExpectedAnnualContribution ?? risk.finalCalculatedMonetaryImpact ?? 0);
          engineRiskCount++;
          // Accumulate per risk-type totals for the breakdown table
          if (risk.aiRiskType) {
            const t = risk.aiRiskType.trim().toUpperCase();
            if (!engineByRiskType[t]) engineByRiskType[t] = { count: 0, subtotal: 0, subtotalLow: 0, subtotalHigh: 0 };
            engineByRiskType[t].count++;
            engineByRiskType[t].subtotal += riskImpact;
            engineByRiskType[t].subtotalLow += Number(risk.impliedExpectedAnnualContributionLow ?? risk.impliedExpectedAnnualContribution ?? risk.finalCalculatedMonetaryImpact ?? 0);
            engineByRiskType[t].subtotalHigh += Number(risk.impliedExpectedAnnualContributionHigh ?? risk.impliedExpectedAnnualContribution ?? risk.finalCalculatedMonetaryImpact ?? 0);
          }
        } else {
          // No engine data — riskImpact stays 0
        }

        // Split total impact across cost categories
        breakdown.direct_costs += riskImpact * 0.30;
        breakdown.business_interruption += riskImpact * 0.25;
        breakdown.recovery_costs += riskImpact * 0.20;
        breakdown.regulatory_fines += riskImpact * 0.15;
        breakdown.reputation_damage += riskImpact * 0.10;
      });

      const totalImpact = totalEngineImpact;
      const totalImpactLow = totalEngineImpactLow;
      const totalImpactHigh = totalEngineImpactHigh;

      // Cost category percentages (fixed allocation model)
      const CATEGORY_PCTS = {
        direct_costs: 0.30,
        business_interruption: 0.25,
        recovery_costs: 0.20,
        regulatory_fines: 0.15,
        reputation_damage: 0.10,
      };
      const breakdown_low = Object.fromEntries(
        Object.entries(CATEGORY_PCTS).map(([k, pct]) => [k, totalImpactLow * pct])
      );
      const breakdown_high = Object.fromEntries(
        Object.entries(CATEGORY_PCTS).map(([k, pct]) => [k, totalImpactHigh * pct])
      );

      const analysisResult = {
        total_potential_impact: Math.round(totalImpact),
        total_potential_impact_low: Math.round(totalImpactLow),
        total_potential_impact_high: Math.round(totalImpactHigh),
        engine_calculated_impact: Math.round(totalEngineImpact),
        engine_calculated_impact_low: Math.round(totalEngineImpactLow),
        engine_calculated_impact_high: Math.round(totalEngineImpactHigh),
        has_scenario_bands: totalEngineImpactLow !== totalEngineImpact || totalEngineImpactHigh !== totalEngineImpact,
        risk_count: risksToAnalyze.length,
        engine_risk_count: engineRiskCount,
        breakdown,
        breakdown_low,
        breakdown_high,
        engine_by_risk_type: engineByRiskType,
        parameters,
        recommendations: generateRecommendations(totalImpact, risksToAnalyze.length),
      };

      setAnalysis(analysisResult);
      setStep("results");
    } catch (error) {
      setStep("setup");
    } finally {
      setLoading(false);
    }
  };


  const { id } = useParams();

  const navigate = useNavigate();

  const [showAssignModal, setShowAssignModal] = useState(false);


  const handleOpenAssignModal = (risk) => {
    setSelectedRisk(risk);
    setShowAssignModal(true);
  };

  // Consolidated data fetching - loads all data together
  useEffect(() => {
    if (!id) return;

    const fetchAllData = async () => {
      setLoading(true);
      setLoadingCompliance(true);
      setLoadingPredictive(true);

      try {
        // Fetch all data in parallel using Promise.all
        const [
          assessmentData,
          risksData,
          analyticsRisks,
          vulnerabilityDist,
          complianceData,
          predictiveRiskData,
          riskCategoryData,
          frameworkData,
          scoring
        ] = await Promise.all([
          apiCall('GET', `/aiAssessments/${id}`),
          apiCall('GET', `/aiAssessments/${id}/risks`),
          apiCall('GET', `/aiRisks/assessment-risks/${[id]}`),
          apiCall('GET', `/aiRisks/vulnerability-distribution/assessment/${[id]}`),
          apiCall('GET', `/aiRisks/compliance-status?assessmentId=${id}&useQuestionCategory=true`),
          apiCall('GET', `/aiRisks/aiAssessment/${id}/predictive-risk`),
          apiCall('GET', `/aiRisks/compliance-status?assessmentId=${id}`),
          apiCall('GET', `/aiAssessments/${id}/framework-summary`).catch(err => {
            return null;
          }),
          apiCall('GET', `/aiAssessments/${id}/scoring`).catch(() => null)
        ]);
        setFrameworkScores(frameworkData);

        // Set assessment and risks
        setAssessment(assessmentData);
        setRisks(risksData);
        // Get verticalId
        if (assessmentData?.verticalId) {
          setVerticalId(assessmentData.verticalId);
        } else if (risksData?.[0]?.questionRiskDetails?.verticalId) {
          setVerticalId(risksData[0].questionRiskDetails.verticalId);
        }

        const analyticsRisksArr = Array.isArray(analyticsRisks) ? analyticsRisks : (analyticsRisks?.risks ?? []);
        setOrganizationAnalytics(computeOrganizationAnalytics(analyticsRisksArr, risksData, scoring));
        setQuantificationFactors(analyticsRisks?.quantificationFactors ?? null);

        // Set compliance data (card 2) — risk-table-based, already excludes remediated risks
        setComplianceData({
          compliance_status: Object.fromEntries(
            Object.entries(complianceData?.risk_status || {}).map(([k, v]) => [k, v || 0])
          ),
          overall_compliance: complianceData?.overall_risk || 0,
        });

        // Set predictive risk data (card 4) — risk-table-based, already excludes remediated risks
        setPredictiveRisk({
          current: predictiveRiskData?.currentScore || 0,
          day45: predictiveRiskData?.predictive45DayScore || 0,
          day90: predictiveRiskData?.predictive90DayScore || 0,
          day180: predictiveRiskData?.predictive180DayScore || 0,
          escalating180: predictiveRiskData?.escalating180Count || 0,
        });

        // Set risk category data (Risk Exposure by Category — grouped by AIRiskCategory)
        setRiskCategories(riskCategoryData?.risk_status || {});
      } catch (error) {
        // Set default values on error
        setOrganizationAnalytics({ overall_risk_score: 0, overall_RiskLevel: 'LOW', total_risks: 0, risk_distribution: {} });
        setComplianceData({ compliance_status: {}, overall_compliance: 0 });
        setPredictiveRisk({ day45: 0, day90: 0, day180: 0 });
      } finally {
        setLoading(false);
        setLoadingCompliance(false);
        setLoadingPredictive(false);
      }
    };

    fetchAllData();
  }, [id]);

  // Keep fetchAssessmentRisks for modal refresh callbacks
  const fetchAssessmentRisks = async () => {
    try {
      const assessmentData = await apiCall('GET', `/aiAssessments/${id}`);
      setAssessment(assessmentData);

      const risksData = await apiCall('GET', `/aiAssessments/${id}/risks`);
      setRisks(risksData);

      if (assessmentData?.verticalId) {
        setVerticalId(assessmentData.verticalId);
      } else if (risksData?.[0]?.questionRiskDetails?.verticalId) {
        setVerticalId(risksData[0].questionRiskDetails.verticalId);
      }
    } catch (error) {}
  };

  useEffect(() => { setPage(1); }, [risks]);
  const [severityFilter, setSeverityFilter] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(RISK_STATUS_FILTER_DEFAULT);
  const [sourcesFilter, setSourcesFilter] = useState('');
  useEffect(() => { setPage(1); }, [severityFilter, domainFilter, categoryFilter, searchTerm, statusFilter, sourcesFilter]);
  useEffect(() => { setSelectedRiskIds(new Set()); }, [severityFilter, domainFilter, categoryFilter, searchTerm, statusFilter, sourcesFilter]);

  const sourceOptions = useMemo(() => [...new Set(risks.map(r => r.riskType || 'AI'))].sort(), [risks]);

  // Breakdown of this assessment's ACTIVE risks by source: native AI findings vs. cross-listed
  // GRC (control-derived) risks. Mirrors the source filter's `riskType || 'AI'`.
  const riskSources = useMemo(() => {
    const active = risks.filter(isActiveRisk);
    const ai = active.filter(r => (r.riskType || 'AI') === 'AI').length;
    const grc = active.filter(r => r.riskType === 'GRC').length;
    return [
      { label: 'AI', count: ai, dot: 'bg-purple-600', text: 'text-purple-700' },
      { label: 'GRC', count: grc, dot: 'bg-cyan-600', text: 'text-cyan-700' },
    ];
  }, [risks]);

  const filteredRisks = useMemo(() => {
    let result = applyRiskStatusFilter(risks, statusFilter);
    if (sourcesFilter) {
      result = result.filter(r => (r.riskType || 'AI') === sourcesFilter);
    }
    if (severityFilter) {
      result = result.filter(r => (r.userRating || r.residualRiskRating || r.inherentRiskRating || '').toUpperCase() === severityFilter);
    }
    if (domainFilter) {
      result = result.filter(r => r.riskCategory === domainFilter);
    }
    if (categoryFilter) {
      result = result.filter(r => r.ownRiskCategory === categoryFilter);
    }
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      result = result.filter(r =>
        (r.riskTitle || '').toLowerCase().includes(t) ||
        (r.riskDescription || '').toLowerCase().includes(t) ||
        (r.riskCategory || '').toLowerCase().includes(t) ||
        (r.aiRiskType || '').toLowerCase().includes(t)
      );
    }
    return result;
  }, [risks, severityFilter, domainFilter, categoryFilter, searchTerm, statusFilter, sourcesFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRisks.length / pageSize));
  const pagedRisks = useMemo(() => {
    const sorted = [...filteredRisks].sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'engineImpact') {
        aVal = a.finalCalculatedMonetaryImpact ?? -Infinity;
        bVal = b.finalCalculatedMonetaryImpact ?? -Infinity;
      } else if (sortBy === 'monetaryImpact') {
        aVal = Number(a.estimatedMonetaryImpact) || 0;
        bVal = Number(b.estimatedMonetaryImpact) || 0;
      } else {
        aVal = a.severityScore ?? 0;
        bVal = b.severityScore ?? 0;
      }
      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    });
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [filteredRisks, page, pageSize, sortBy, sortOrder]);

  const exportReport = async (format) => {
    try {
      // Use the correct endpoint with query parameter
      const response = await apiCall('GET', `/aiReports/assessment/${id}?format=${format.toLowerCase()}`);

      if (format.toUpperCase() === 'EXCEL' && response.excel_data && response.filename) {
        // Handle Excel export with base64 data
        const link = document.createElement('a');
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.excel_data}`;
        link.download = response.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (format.toUpperCase() === 'PDF' && response.pdf_data && response.filename) {
        // Handle PDF export with base64 data
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${response.pdf_data}`;
        link.download = response.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (response.download_url) {
        // Fallback for old download_url format (if still supported)
        const link = document.createElement('a');
        link.href = response.download_url;
        link.download = `assessment_report_${id}.${format.toLowerCase()}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('No valid report data received');
      }
    } catch (error) {
      alert(`Failed to export ${format} report. Please try again.`);
    }
  };



  const getRiskColor = (InherentRiskRating) => {
    switch (InherentRiskRating) {
      case 'CRITICAL': return 'bg-red-100 text-red-700 border-red-500';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-500';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-500';
      case 'LOW': return 'bg-green-100 text-green-800 border-green-500';
      default: return 'bg-gray-100 text-gray-800 border-gray-500';
    }
  };

  const getFrameworkColor = (framework) => {
    const colors = {
      "SOC 2": "blue",
      "NIST CSF 2.0": "green",
      "ISO 27001:2022": "purple",
      "CIS Controls v8": "orange",
      "PCI DSS v4": "red",
      "HIPAA": "pink",
      "GDPR": "indigo",
      "DORA": "purple",
      "FedRAMP / 800-53": "yellow",
      "NIST 800-171": "teal",
      "CMMC L2": "cyan",
      "HITRUST CSF": "gray",
      "NIST AI RMF": "emerald",
      "ISO 42001": "violet",
      "OWASP LLM Top 10": "rose",
      "MITRE ATLAS": "amber",
      "CSA AI Controls": "sky",
    }
    return colors[framework] || "blue"
  }



  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">

        {/* Context info banner */}
        {assessment && (
          <AssessmentContextHeader
            regionName={regionName}
            orgName={orgName}
            dealName={dealName}
            businessUnitName={businessUnitName}
          />
        )}

        <div className="mb-8 flex justify-between items-center">
          <div>
            {/* 
            <button
              onClick={() => navigate('/AIDeals')}
              className="flex items-center text-purple-600 hover:text-purple-800 mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to AI Deals
            </button>
            */}
            <h1 className="text-3xl font-bold text-content-primary">AI Assessment Risks & Controls</h1>
            <p className="mt-2 text-gray-600">
              {countActiveRisks(risks)} active risks
            </p>
          </div>

          <div className="flex space-x-3">
            {/* Monetary Analysis Button */}
            <Button
              variant="secondary"
              onClick={() => false && setShowMonetaryAnalysis(true)} style={{display:'none'}}
              disabled={!assessment || !assessment.aiAssessmentID}
              title={!assessment ? "Assessment loading..." : "Start analysis"}
            >

              <DollarSign className="h-4 w-4 mr-2" />
              Monetary Analysis
            </Button>

            <Button
              variant="secondary"
              onClick={() => exportReport('PDF')}
            >
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
            <Button
              variant="secondary"
              onClick={() => exportReport('EXCEL')}
            >
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </div>

        {/* Risk Summary */}
        {/*
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(level => (
            <Card key={level} className="text-center">
              <div
                className={`text-2xl font-bold ${level === 'CRITICAL'
                  ? 'text-red-600'
                  : level === 'HIGH'
                    ? 'text-orange-600'
                    : level === 'MEDIUM'
                      ? 'text-yellow-600'
                      : 'text-green-600'
                  }`}
              >
                {
                  risks.filter(r => {
                    const rating = (r.userRating || r.residualRiskRating || r.inherentRiskRating || '').toUpperCase();
                    return rating === level;
                  }).length
                }
              </div>
              <div className="text-sm text-gray-600">{level} Risks</div>
            </Card>
          ))}
        </div>
        */}

        <div>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <ControlEffectivenessCard analytics={organizationAnalytics} />
                <ResidualRiskCard analytics={organizationAnalytics} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
                <OverallRiskScoreCard analytics={organizationAnalytics} title="AI Risk Score" />
                <ComplianceStatusCard
                  analytics={complianceData}
                  organizationAnalytics={organizationAnalytics}
                  activeDomain={domainFilter}
                  onDomainClick={(domain) => setDomainFilter(prev => prev === domain ? '' : domain)}
                />
                {/* Vulnerability Distribution stacked over the Risk Sources breakdown;
                    the other cards in this row stretch to span both. */}
                <div className="flex flex-col gap-4">
                  <VulnerabilityDistributionCard analytics={organizationAnalytics} />
                  <RiskSourceBreakdownCard sources={riskSources} />
                </div>
                <PredictiveRiskBars
                  data={predictiveRisk}
                  loading={loadingPredictive}
                  analytics={organizationAnalytics}
                />
              </div>
            </>
          )}
        </div>
        {/* Risk Exposure by Category */}
        <div className="mb-4">
          <RiskCategoryBarChart
            riskData={riskCategories}
            activeDomain={categoryFilter}
            onDomainClick={(domain) => setCategoryFilter(prev => prev === domain ? '' : domain)}
          />
        </div>

        {showFrameworkRiskExposure && (
          <div className="mb-3">
            <FrameworkComplianceBars frameworkScores={frameworkScores} />
          </div>
        )}

        {/* FAIR-Aligned Quantification */}
        <div className="mb-3">
          <FairFactorTree quantificationFactors={quantificationFactors} />
        </div>
        <div className="mb-3">
          <FairMethodologyDisclaimer />
        </div>

        {/* Predicive Timeline disclaimer info */}
        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-700 mb-2">
            ⓘ Predictive Insight & Financial impact forecast
          </h4>
          <p className="text-gray-700 text-sm mb-2">
            The predictive timelines represent the most likely attack path based on current
            risk signals and identified control gaps. Actual incidents may occur faster,
            slower, or follow different paths depending on attacker behavior, evolving
            threat conditions, and your specific security environment.
          </p>
          <p className="text-gray-600 text-xs">
            <span className="font-semibold">Financial impact forecast:</span> These figures represent
            <span className="font-semibold"> scenario-based exposure projections</span>,
            not guaranteed loss predictions. Actual financial impact may vary depending
            on incident scope, response effectiveness, regulatory involvement, and
            real-world conditions at the time of an event.
          </p>
        </div>


        {/* Risks List */}
              {/* Sort controls */}
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-sm text-gray-600 font-medium">Sort by:</span>
                {[
                  { value: 'severity', label: 'Severity Score' },
                  { value: 'engineImpact', label: '💹 Engine Impact' },
                  { value: 'monetaryImpact', label: 'AI Impact' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      if (sortBy === opt.value) {
                        setSortOrder(o => o === 'desc' ? 'asc' : 'desc');
                      } else {
                        setSortBy(opt.value);
                        setSortOrder('desc');
                      }
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${sortBy === opt.value
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                      }`}
                  >
                    {opt.label} {sortBy === opt.value ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                  </button>
                ))}
              </div>

        {selectedRiskIds.size > 0 && (
          <div className={`sticky ${PINNED_PANEL_TOP} z-20 flex items-center gap-3 mb-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg shadow-sm`}>
            <span className="text-sm font-medium text-blue-800">
              {selectedRiskIds.size} risk{selectedRiskIds.size !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setShowBulkAssignModal(true)}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 flex items-center gap-1.5"
            >
              <Shield className="h-4 w-4" />
              Assign Selected
            </button>
            <button onClick={() => setSelectedRiskIds(new Set())} className="text-sm text-blue-600 hover:text-blue-800">Clear</button>
          </div>
        )}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            {canManageRisks && filteredRisks.length > 0 && (
              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 cursor-pointer"
                  checked={selectedRiskIds.size === filteredRisks.length && filteredRisks.length > 0}
                  ref={el => { if (el) el.indeterminate = selectedRiskIds.size > 0 && selectedRiskIds.size < filteredRisks.length; }}
                  onChange={toggleSelectAll}
                />
                <span className="text-sm text-gray-600">Select All</span>
              </label>
            )}
            <span className="text-sm text-gray-600">
              Showing {filteredRisks.length} of {risks.length} risks
              {(severityFilter || domainFilter || categoryFilter || searchTerm || sourcesFilter || statusFilter !== 'all') && <span className="ml-1 text-gray-400">(filtered)</span>}
            </span>
            {domainFilter && (
              <button
                onClick={() => setDomainFilter('')}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium hover:bg-indigo-200 transition-colors"
              >
                {domainFilter}
                <span className="ml-0.5 text-indigo-500 hover:text-indigo-700">✕</span>
              </button>
            )}
            {categoryFilter && (
              <button
                onClick={() => setCategoryFilter('')}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium hover:bg-purple-200 transition-colors"
              >
                {categoryFilter}
                <span className="ml-0.5 text-purple-500 hover:text-purple-700">✕</span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search risks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-56 border border-gray-300 rounded-md py-1.5 text-sm"
              />
            </div>
            <select
              value={domainFilter}
              onChange={e => setDomainFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white"
            >
              <option value="">All Domains</option>
              {Object.keys(complianceData?.compliance_status || {}).sort().map(domain => (
                <option key={domain} value={domain}>{domain}</option>
              ))}
            </select>
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white"
            >
              <option value="">All Categories</option>
              {Object.keys(riskCategories || {}).sort().map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <select
              value={severityFilter}
              onChange={e => setSeverityFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white"
            >
              <option value="">All Severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
            <RiskSourcesFilter
              value={sourcesFilter}
              onChange={setSourcesFilter}
              sources={sourceOptions}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white"
            />
            <RiskStatusFilter
              value={statusFilter}
              onChange={setStatusFilter}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white"
            />
          </div>
        </div>
              <div className="space-y-6" ref={listTopRef}>
                {pagedRisks.map((risk) => {
                  // Normalize severity and calculate weighted score
                  const severity = (risk.userRating || risk.residualRiskRating || risk.inherentRiskRating || 'LOW').toUpperCase();
                  const weightedScore = risk.severityScore || 0;
                  return (
                    <Card key={risk.id} className={`${risk.status === 'accepted' ? 'bg-gray-50' : ''}`}>
                      {canManageRisks && (
                        <input
                          type="checkbox"
                          className="h-4 w-4 mb-2 text-blue-600 rounded border-gray-300 cursor-pointer"
                          checked={selectedRiskIds.has(risk.id)}
                          onChange={e => { e.stopPropagation(); toggleRiskSelection(risk.id); }}
                        />
                      )}
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-1">
                            <h3 className="text-lg font-semibold">{risk.riskTitle}</h3>
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Severity with weighted score */}
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium border ${getRiskColor(severity)}`}
                              >
                                {severity} ({weightedScore}%)
                              </span>
                              {risk.status === 'accepted' && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">
                                  Accepted
                                </span>
                              )}
                              {risk.status === 'updated' && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                  Updated
                                </span>
                              )}
                              <RiskStatusBadge risk={risk} className="flex-shrink-0" />
                            </div>
                          </div>
                          {(risk.riskCategory || risk.ownRiskCategory) && (
                            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                              {risk.riskCategory && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-gray-400">Risk Vector:</span>
                                  <button
                                    onClick={() => setDomainFilter(prev => prev === risk.riskCategory ? '' : risk.riskCategory)}
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${domainFilter === risk.riskCategory ? 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300' : 'bg-gray-100 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600'}`}
                                    title={`Filter by: ${risk.riskCategory}`}
                                  >
                                    {risk.riskCategory}
                                  </button>
                                </div>
                              )}
                              {risk.ownRiskCategory && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-gray-400">Category:</span>
                                  <button
                                    onClick={() => setCategoryFilter(prev => prev === risk.ownRiskCategory ? '' : risk.ownRiskCategory)}
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${categoryFilter === risk.ownRiskCategory ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-300' : 'bg-gray-100 text-gray-600 hover:bg-purple-50 hover:text-purple-600'}`}
                                    title={`Filter by category: ${risk.ownRiskCategory}`}
                                  >
                                    {risk.ownRiskCategory}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          {(risk.assignedToUserName || risk.assignedToEmail) && (
                            <div className="text-sm text-gray-700">
                              Assigned To:{" "}
                              <span className="font-medium text-blue-700">
                                {risk.assignedToUserName || risk.assignedToEmail}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-start gap-2 shrink-0 ml-3">
                          <div className="text-right">
                            {isFairAligned(risk.financialEngineVersion) && risk.finalCalculatedMonetaryImpact != null ? (
                              risk.impactLow != null && risk.impactHigh != null ? (
                                <>
                                  <div
                                    className="text-lg font-bold text-emerald-700 cursor-help"
                                    title="Range reflects modeled uncertainty across operational and regulatory scenarios."
                                  >
                                    {formatImpact(risk.impactLow)} – {formatImpact(risk.impactHigh)}+
                                  </div>
                                  <div className="text-xs text-emerald-600">
                                    Most Likely: {formatImpact(risk.finalCalculatedMonetaryImpact)}
                                  </div>
                                  <div className="text-xs text-gray-400">Engine-Calculated Range</div>
                                </>
                              ) : (
                                <>
                                  <div className="text-lg font-bold text-emerald-700">
                                    ${Math.round(Number(risk.finalCalculatedMonetaryImpact)).toLocaleString()}
                                  </div>
                                  <div className="text-xs text-gray-500">Engine-Calculated Impact</div>
                                </>
                              )
                            ) : (
                              <>
                                <div className="text-lg font-bold text-content-primary">
                                  ${Math.round(Number(risk.estimatedMonetaryImpact))?.toLocaleString() || '0'}
                                </div>
                                <div className="text-sm text-gray-600">Potential Impact</div>
                              </>
                            )}
                          </div>
                          <button
                            onClick={() => toggleCard(risk.id)}
                            className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                            title={expandedCards[risk.id] ? 'Collapse details' : 'Expand details'}
                          >
                            {expandedCards[risk.id]
                              ? <ChevronDown size={18} />
                              : <ChevronRight size={18} />
                            }
                            <span className="text-xs leading-none">{expandedCards[risk.id] ? 'Hide' : 'Details'}</span>
                          </button>
                        </div>
                      </div>
                      {/* Security Frameworks (Only impacted ones) */}
                      <div className="flex gap-2 overflow-x-auto pb-1 mb-1">
                        <h3 className="text-lg font-semibold mr-4 shrink-0">Framework:</h3>
                        {Object.keys(risk.frameworkImpact || {})
                          // only frameworks with mapped controls — excludes both empty values and
                          // "N/A - ..." scoping notes (see isFrameworkNotApplicable)
                          .filter(f => risk.frameworkImpact?.[f] && !isFrameworkNotApplicable(risk.frameworkImpact[f]))
                          .map((framework) => (
                            <FrameworkCard
                              key={`${risk.id}-framework-${framework}`}
                              title={framework}
                              controls={risk.frameworkImpact?.[framework] || ""}
                              color={getFrameworkColor(framework)}
                            />
                          ))}
                      </div>
                      <p className="text-[10px] text-gray-400 italic mb-4">
                        click + on any framework to see all its mapped controls
                      </p>

                      {/* ── Three-tab layout ── */}
                      {expandedCards[risk.id] && (() => {
                        const activeTab = riskActiveTabs[risk.id] || 'overview';
                        const setTab = (tab) =>
                          setRiskActiveTabs(prev => ({ ...prev, [risk.id]: tab }));

                        const tabs = [
                          { id: 'overview',  label: 'Overview',        icon: '📋' },
                          { id: 'evolution', label: 'What May Break',  icon: '📈' },
                          { id: 'financial', label: 'Financial Impact', icon: '💰' },
                          { id: 'log',       label: 'Activity Log',     icon: '🕐' },
                        ];

                        return (
                          <>
                            {/* Tab bar — Chrome-style tray */}
                            <div className="flex items-end gap-0.5 bg-slate-100 border border-gray-200 border-b-0 rounded-t-xl px-2 pt-2">
                              {tabs.map(t => {
                                const isActive = activeTab === t.id;
                                return (
                                  <button
                                    key={t.id}
                                    onClick={() => setTab(t.id)}
                                    className={`relative flex items-center gap-2 px-5 py-2.5 text-xs font-semibold rounded-t-lg -mb-px transition-all duration-150 focus:outline-none border ${
                                      isActive
                                        ? 'bg-white text-indigo-700 border-gray-200 border-b-white z-10 shadow-[0_-1px_3px_rgba(0,0,0,0.06)]'
                                        : 'bg-transparent border-transparent text-gray-500 hover:bg-white/70 hover:text-gray-700'
                                    }`}
                                    style={isActive ? { borderBottomColor: '#ffffff' } : {}}
                                  >
                                    <span className="text-sm leading-none">{t.icon}</span>
                                    <span>{t.label}</span>
                                  </button>
                                );
                              })}
                              {/* Right filler to complete the tray */}
                              <div className="flex-1 border-b border-gray-200 mb-0 pb-0 self-end h-[1px]" />
                            </div>

                            {/* Content panel — seamlessly connected to active tab */}
                            <div className="border border-gray-200 border-t-0 rounded-b-xl rounded-tr-xl bg-white p-4 mb-1">
                            {/* ── Tab 1: Overview ── */}
                            {activeTab === 'overview' && (
                              <div>
                                {/* Risk Description */}
                                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                  <h4 className="font-semibold text-gray-700 mb-2">📋 Risk Description</h4>
                                  <p className="text-gray-700 text-sm">{risk.riskDescription}</p>
                                </div>

                                {/* Crisis Narrative */}
                                {risk.dbCrisisNarrative && (
                                  <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
                                    <h4 className="font-semibold text-red-900 mb-2 flex items-center">
                                      ⚠️ Crisis Narrative
                                    </h4>
                                    <p className="text-gray-700 text-sm">{risk.dbCrisisNarrative}</p>
                                  </div>
                                )}

                                {/* Resolution & Best Practice */}
                                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                                    ✅ Resolution & Expected Best Practice
                                  </h4>
                                  <p className="text-sm text-gray-700">{risk.dbExpectedBestPractice}</p>
                                  {(() => {
                                    const cleanedSteps = parseRemediationSteps(risk?.dbStepByStepRemediation || risk?.stepByStepRemediation);
                                    if (cleanedSteps) {
                                      return (
                                        <div className="space-y-1 mt-2">
                                          {cleanedSteps.map((s, i) => (
                                            <div key={i} className="text-sm text-gray-800">{i + 1}. {s}</div>
                                          ))}
                                        </div>
                                      );
                                    }
                                    return <p className="text-gray-500 text-sm mt-2">No remediation steps available.</p>;
                                  })()}
                                </div>

                                {/* Remediation Trigger */}
                                {risk.dbAxionRemediationTrigger && (
                                  <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                    <h4 className="font-semibold text-purple-900 mb-2 flex items-center">
                                      🎯 Remediation Trigger
                                    </h4>
                                    <RemediationTriggerList trigger={risk.dbAxionRemediationTrigger} />
                                  </div>
                                )}
                              </div>
                            )}

                            {/* ── Tab 2: What May Break – Risk Evolution Stories ── */}
                            {activeTab === 'evolution' && (
                              <div>
                                {(risk.riskEvolutionStory || risk?.dbPredictive45Day || risk?.dbPredictive90Day || risk?.dbPredictive180Day) ? (
                                  <div className="p-4 rounded-xl border bg-gradient-to-br from-gray-50 to-gray-100 mb-4">
                                    <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                                      📈 What May Break - Risk Evolution Stories
                                    </h4>
                                    {risk.riskEvolutionStory && (
                                      <div className="mb-3">
                                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">AI Analysis</p>
                                        <div className="flex items-start gap-3 p-3 bg-orange-50 border border-yellow-200 rounded-lg">
                                          <p className="text-sm text-gray-700">{risk.riskEvolutionStory}</p>
                                        </div>
                                      </div>
                                    )}
                                    <div className="space-y-3">
                                      {risk?.dbPredictive45Day && (
                                        <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                          <div className="text-yellow-600 text-lg">🟡</div>
                                          <div>
                                            <div className="text-xs font-semibold text-yellow-700 uppercase tracking-wide">Day 45 – Early Warning</div>
                                            <p className="text-sm text-gray-700 mt-1">{risk?.dbPredictive45Day || "No data available"}</p>
                                          </div>
                                        </div>
                                      )}
                                      {risk?.dbPredictive90Day && (
                                        <div className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                          <div className="text-orange-600 text-lg">🟠</div>
                                          <div>
                                            <div className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Day 90 – Active Threat Emerging</div>
                                            <p className="text-sm text-gray-700 mt-1">{risk?.dbPredictive90Day || "No data available"}</p>
                                          </div>
                                        </div>
                                      )}
                                      {risk?.dbPredictive180Day && (
                                        <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                          <div className="text-red-600 text-lg">🔴</div>
                                          <div>
                                            <div className="text-xs font-semibold text-red-700 uppercase tracking-wide">Day 180 – Likely Incident / Breach</div>
                                            <p className="text-sm text-gray-700 mt-1">{risk?.dbPredictive180Day || "No data available"}</p>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-6 text-center text-gray-400 text-sm">
                                    No risk evolution data available for this risk.
                                  </div>
                                )}
                              </div>
                            )}

                            {/* ── Tab 3: Financial Impact ── */}
                            {activeTab === 'financial' && (
                              <div>
                                {/* Monetary Impact Analysis */}
                                {isFairAligned(risk.financialEngineVersion) && risk.finalCalculatedMonetaryImpact != null && (
                                  <div className="mb-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                                    <h4 className="font-medium text-emerald-900 mb-3 flex items-center gap-2 flex-wrap">
                                      💰 Monetary Impact Analysis
                                      {risk.impactLow != null && risk.impactHigh != null ? (
                                        <span className="font-bold text-emerald-700">
                                          {formatImpact(risk.impactLow)} – {formatImpact(risk.impactHigh)}+
                                          <span className="ml-1 text-sm font-normal text-emerald-600">
                                            (Most Likely: {formatImpact(risk.finalCalculatedMonetaryImpact)})
                                          </span>
                                        </span>
                                      ) : (
                                        <span className="font-bold text-emerald-700">
                                          ${Math.round(Number(risk.finalCalculatedMonetaryImpact)).toLocaleString()}
                                        </span>
                                      )}
                                    </h4>
                                    {(() => {
                                      const baseExp  = Number(risk.finalCalculatedMonetaryImpact ?? 0);
                                      const baseLow  = Number(risk.impactLow  ?? risk.finalCalculatedMonetaryImpact ?? 0);
                                      const baseHigh = Number(risk.impactHigh ?? risk.finalCalculatedMonetaryImpact ?? 0);
                                      const hasBands = risk.impactLow != null && risk.impactHigh != null && baseLow !== baseHigh;
                                      const cats = [
                                        { label: 'Direct Costs',      pct: 0.30, color: 'text-red-600',    badge: 'bg-red-50 border-red-200'    },
                                        { label: 'Bus. Interruption', pct: 0.25, color: 'text-orange-600', badge: 'bg-orange-50 border-orange-200'},
                                        { label: 'Recovery Costs',    pct: 0.20, color: 'text-blue-600',   badge: 'bg-blue-50 border-blue-200'  },
                                        { label: 'Regulatory Fines',  pct: 0.15, color: 'text-purple-600', badge: 'bg-purple-50 border-purple-200'},
                                        { label: 'Reputation Damage', pct: 0.10, color: 'text-pink-600',   badge: 'bg-pink-50 border-pink-200'  },
                                      ];
                                      return (
                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                          {cats.map(({ label, pct, color, badge }) => {
                                            const exp  = Math.max(25_000, Math.round(baseExp  * pct));
                                            const low  = Math.max(25_000, Math.round(baseLow  * pct));
                                            const high = Math.max(25_000, Math.round(baseHigh * pct));
                                            return (
                                              <div key={label} className={`text-center p-2 rounded border ${badge}`}>
                                                <div className="text-xs font-semibold text-gray-600 mb-1">{label}</div>
                                                {hasBands ? (
                                                  <>
                                                    <div className={`text-sm font-bold ${color} leading-tight`}>{formatImpact(low)} – {formatImpact(high)}+</div>
                                                    <div className="text-xs text-gray-400 mt-0.5">ML: {formatImpact(exp)}</div>
                                                  </>
                                                ) : (
                                                  <div className={`text-base font-bold ${color}`}>{formatImpact(exp)}</div>
                                                )}
                                                <div className="text-xs text-gray-400 mt-0.5">{Math.round(pct * 100)}%</div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}

                                {/* Engine-Calculated Financial Impact */}
                                <FinancialImpactSection risk={risk} />

                                {/* Scenario Exposure Bands */}
                                {false && risk.finalCalculatedMonetaryImpact != null && (() => {
                                  const conservative = Number(risk.finalCalculatedMonetaryImpact);
                                  const combinedFromParts =
                                    risk.sizeMultiplierApplied != null &&
                                    risk.revenueMultiplierApplied != null &&
                                    risk.regulatoryMultiplierApplied != null
                                      ? risk.sizeMultiplierApplied * risk.revenueMultiplierApplied * risk.regulatoryMultiplierApplied
                                      : null;
                                  const appliedMult  = combinedFromParts ?? risk.appliedMultiplier ?? 1.8;
                                  const divisor      = appliedMult > 0 ? appliedMult : 1.8;
                                  const uncappedBase = conservative / divisor;
                                  let moderate = uncappedBase * 2.5;
                                  let severe   = uncappedBase * 3.5;
                                  if (!isFinite(moderate) || isNaN(moderate)) moderate = conservative;
                                  if (!isFinite(severe)   || isNaN(severe))   severe   = moderate;
                                  moderate = Math.max(moderate, conservative);
                                  severe   = Math.max(severe,   moderate);
                                  const hasBackendBands = risk.impactLow != null && risk.impactHigh != null;
                                  const conLow  = hasBackendBands ? Number(risk.impactLow)  : null;
                                  const conHigh = hasBackendBands ? Number(risk.impactHigh) : null;
                                  const uncLow  = conLow  != null ? conLow  / divisor : null;
                                  const uncHigh = conHigh != null ? conHigh / divisor : null;
                                  const modLow  = uncLow  != null ? Math.max(uncLow  * 2.5, conLow)  : null;
                                  const modHigh = uncHigh != null ? uncHigh * 2.5 : null;
                                  const sevLow  = uncLow  != null ? Math.max(uncLow  * 3.5, moderate) : null;
                                  const sevHigh = uncHigh != null ? uncHigh * 3.5 : null;
                                  const headerLow  = conLow  ?? conservative;
                                  const headerHigh = sevHigh ?? severe;
                                  const renderBand = (lo, hi, ml, color) =>
                                    lo != null && hi != null ? (
                                      <div className="text-right">
                                        <div className={`text-sm font-bold ${color} leading-tight`}>{formatImpact(lo)} – {formatImpact(hi)}+</div>
                                        <div className="text-xs text-gray-400 mt-0.5">ML: {formatImpact(ml)}</div>
                                      </div>
                                    ) : (
                                      <span className={`text-sm font-bold ${color}`}>{formatImpact(ml)}</span>
                                    );
                                  const isOpen = !!openScenarioBands[risk.id];
                                  return (
                                    <div className="mb-4 rounded-lg border border-amber-200 overflow-hidden">
                                      <button
                                        onClick={() => toggleScenarioBands(risk.id)}
                                        className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 hover:bg-amber-100 transition-colors text-left"
                                      >
                                        <div className="flex items-center gap-2">
                                          {isOpen
                                            ? <ChevronDown  size={16} className="text-amber-700 flex-shrink-0" />
                                            : <ChevronRight size={16} className="text-amber-700 flex-shrink-0" />
                                          }
                                          <span className="text-sm font-semibold text-amber-900">📊 Scenario Exposure Bands</span>
                                        </div>
                                        <span className="ml-2 flex-shrink-0 text-right">
                                          <span className="text-sm font-bold text-emerald-700">{formatImpact(headerLow)}</span>
                                          <span className="text-gray-400 mx-1">–</span>
                                          <span className="text-sm font-bold text-red-600">{formatImpact(headerHigh)}+</span>
                                        </span>
                                      </button>
                                      {isOpen && (
                                        <div className="px-4 py-4 bg-white space-y-2">
                                          <div className="flex items-center justify-between px-3 py-2 rounded border bg-emerald-50 border-emerald-200">
                                            <span className="text-xs font-semibold text-emerald-800">Conservative</span>
                                            {renderBand(conLow, conHigh, conservative, 'text-emerald-700')}
                                          </div>
                                          <div className="flex items-center justify-between px-3 py-2 rounded border bg-amber-50 border-amber-200">
                                            <span className="text-xs font-semibold text-amber-800">Moderate Exposure</span>
                                            {renderBand(modLow, modHigh, moderate, 'text-amber-600')}
                                          </div>
                                          <div className="flex items-center justify-between px-3 py-2 rounded border bg-red-50 border-red-200">
                                            <span className="text-xs font-semibold text-red-800">Severe Exposure</span>
                                            {renderBand(sevLow, sevHigh, severe, 'text-red-600')}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {risk.finalCalculatedMonetaryImpact == null && (
                                  <div className="p-6 text-center text-gray-400 text-sm">
                                    No financial impact data available for this risk.
                                  </div>
                                )}
                              </div>
                            )}
                            {activeTab === 'log' && (
                              <ActivityLogTab riskId={risk.id} riskType="AI" />
                            )}
                            </div>{/* end content panel */}
                          </>
                        );
                      })()}



                      {/* Action Buttons */}
                      <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                        {canManageRisks && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={selectedRiskIds.size > 0}
                            title={selectedRiskIds.size > 0 ? 'Clear or assign the current selection first' : undefined}
                            onClick={() => handleOpenAssignModal(risk)}
                          >
                            <Shield className="h-4 w-4 mr-2" />
                            {(risk.assignedToUserName || risk.assignedToEmail) ? "Reassign Risk" : "Assign Risk"}
                          </Button>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>

        <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={filteredRisks.length}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            scrollTargetRef={listTopRef}
        />

        {/* Monetary Impact Analysis Service */}

        {false && showMonetaryAnalysis && (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "#fff",
              padding: "24px",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              zIndex: 9999,
              width: "80%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            {/* ------------------ Monetary Impact Analysis UI ------------------ */}
            {(() => {
              if (step === "setup") {
                return (
                  <div className="p-6 max-w-5xl mx-auto">
                    <h2 className="text-2xl font-bold mb-4">
                      Monetary Impact Analysis Setup
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Configure company profile parameters before running the analysis.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      {/* Company Size */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Company Size
                        </label>
                        <select
                          className="w-full px-3 py-2 border rounded-md"
                          value={parameters.companySize}
                          onChange={(e) =>
                            setParameters({
                              ...parameters,
                              companySize: e.target.value,
                            })
                          }
                        >
                          <option value="small">Small (1-50 employees)</option>
                          <option value="medium">Medium (51-500 employees)</option>
                          <option value="large">Large (501-5000 employees)</option>
                          <option value="enterprise">
                            Enterprise (5000+ employees)
                          </option>
                        </select>
                      </div>

                      {/* Annual Revenue */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Annual Revenue
                        </label>
                        <select
                          className="w-full px-3 py-2 border rounded-md"
                          value={parameters.annualRevenue}
                          onChange={(e) =>
                            setParameters({
                              ...parameters,
                              annualRevenue: e.target.value,
                            })
                          }
                        >
                          <option value="<1M">Less than $1M</option>
                          <option value="1-10M">$1M - $10M</option>
                          <option value="10-50M">$10M - $50M</option>
                          <option value="50-100M">$50M - $100M</option>
                          <option value=">100M">More than $100M</option>
                        </select>
                      </div>

                      {/* Critical Systems */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Critical Systems Dependency
                        </label>
                        <select
                          className="w-full px-3 py-2 border rounded-md"
                          value={parameters.criticalSystems}
                          onChange={(e) =>
                            setParameters({
                              ...parameters,
                              criticalSystems: e.target.value,
                            })
                          }
                        >
                          <option value="low">Low</option>
                          <option value="moderate">Moderate</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>

                      {/* Regulatory Requirements */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Regulatory Requirements
                        </label>
                        <select
                          className="w-full px-3 py-2 border rounded-md"
                          value={parameters.regulatoryRequirements}
                          onChange={(e) =>
                            setParameters({
                              ...parameters,
                              regulatoryRequirements: e.target.value,
                            })
                          }
                        >
                          <option value="minimal">Minimal</option>
                          <option value="standard">Standard</option>
                          <option value="strict">Strict (HIPAA, SOX, etc.)</option>
                          <option value="highly-regulated">
                            Highly Regulated (Financial, Healthcare)
                          </option>
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button onClick={calculateMonetaryImpact}>
                        Start Analysis {/*($299)*/}
                      </Button>
                      <Button
                        onClick={() => setShowMonetaryAnalysis(false)}
                        variant="outline"
                        style={{ marginLeft: "10px" }}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                );
              }

              if (step === "analyzing") {
                return (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <h3 className="text-lg font-medium mb-2">
                      Calculating Risk-Based Monetary Impact
                    </h3>
                    <p className="text-gray-600">
                      Analyzing {assessment?.name} risks and estimating potential
                      financial impact...
                    </p>
                  </div>
                );
              }

              if (step === "results" && analysis) {
                return (
                  <div className="p-6 max-w-5xl mx-auto space-y-6">
                    <h2 className="text-2xl font-bold">
                     AI Monetary Impact Analysis Results
                    </h2>

                    {/* ── Portfolio Scenario Exposure Cards (frontend-computed) ── */}
                    {(() => {
                      const conML   = analysis.total_potential_impact;
                      const conLow  = analysis.has_scenario_bands ? analysis.total_potential_impact_low  : null;
                      const conHigh = analysis.has_scenario_bands ? analysis.total_potential_impact_high : null;

                      // Uncap the portfolio total using the engine cap (1.8×) to derive scenario bases
                      const div = 1.8;
                      const uncML   = conML  / div;
                      const uncLow  = conLow  != null ? conLow  / div : null;
                      const uncHigh = conHigh != null ? conHigh / div : null;

                      let modML = uncML * 2.5;
                      let sevML = uncML * 3.5;
                      if (!isFinite(modML) || isNaN(modML)) modML = conML;
                      if (!isFinite(sevML) || isNaN(sevML)) sevML = modML;
                      modML = Math.max(modML, conML);
                      sevML = Math.max(sevML, modML);

                      const modLow  = uncLow  != null ? Math.max(uncLow  * 2.5, conLow)  : null;
                      const modHigh = uncHigh != null ? uncHigh * 2.5 : null;
                      const sevLow  = uncLow  != null ? Math.max(uncLow  * 3.5, modML)   : null;
                      const sevHigh = uncHigh != null ? uncHigh * 3.5 : null;

                      // Renders Low–High range with ML subtitle, or single value
                      const scenarioRange = (lo, hi, ml, mainColor, subColor) => (
                        lo != null && hi != null ? (
                          <>
                            <div className={`text-xl font-bold ${mainColor} leading-tight`}>
                              {formatImpact(lo)} – {formatImpact(hi)}+
                            </div>
                            <div className={`text-xs ${subColor} mt-0.5`}>
                              ML: {formatImpact(ml)}
                            </div>
                          </>
                        ) : (
                          <div className={`text-xl font-bold ${mainColor}`}>
                            {formatImpact(ml)}
                          </div>
                        )
                      );

                      return (
                        <div className="grid grid-cols-3 gap-3">
                          {/* Conservative */}
                          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                            <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">
                              🛡️ Conservative
                            </div>
                            
                            {scenarioRange(conLow, conHigh, conML, 'text-emerald-700', 'text-emerald-500')}
                          </div>

                          {/* Moderate */}
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">
                              ⚠️ Moderate
                            </div>
                           
                            {scenarioRange(modLow, modHigh, modML, 'text-amber-600', 'text-amber-500')}
                          </div>

                          {/* Severe */}
                          <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                            <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">
                              🔴 Severe
                            </div>
                            
                            {scenarioRange(sevLow, sevHigh, sevML, 'text-red-600', 'text-red-500')}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Total impact hero */}
                    <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                      <h3 className="text-xl font-bold mb-2">Total Potential Impact</h3>
                      {analysis.has_scenario_bands ? (
                        <>
                          <div className="text-4xl font-bold text-red-600 mb-1">
                            {formatImpact(analysis.total_potential_impact_low)} – {formatImpact(analysis.total_potential_impact_high)}+
                          </div>
                          <div className="text-lg text-red-500 mb-2">
                            Most Likely: ${analysis.total_potential_impact.toLocaleString()}
                          </div>
                        </>
                      ) : (
                        <div className="text-4xl font-bold text-red-600 mb-2">
                          ${analysis.total_potential_impact.toLocaleString()}
                        </div>
                      )}
                      <p className="text-gray-600">
                        Based on {analysis.risk_count} identified risks in {assessment?.name}
                      </p>
                      {analysis.has_scenario_bands && (
                        <p className="text-xs text-gray-400 mt-1">
                          Range reflects modeled uncertainty across operational and regulatory scenarios.
                        </p>
                      )}
                    </div>

                    {/* Engine vs fallback source breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-emerald-700 font-semibold text-sm">💹 Engine-Calculated</span>
                          <span className="px-1.5 py-0.5 bg-emerald-200 text-emerald-800 text-xs rounded">MonetaryImpactEngine</span>
                        </div>
                        {analysis.has_scenario_bands ? (
                          <>
                            <div className="text-2xl font-bold text-emerald-700">
                              {formatImpact(analysis.engine_calculated_impact_low)} – {formatImpact(analysis.engine_calculated_impact_high)}+
                            </div>
                            <div className="text-sm text-emerald-600 mt-0.5">
                              Most Likely: ${analysis.engine_calculated_impact.toLocaleString()}
                            </div>
                          </>
                        ) : (
                          <div className="text-2xl font-bold text-emerald-700">
                            ${analysis.engine_calculated_impact.toLocaleString()}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          {analysis.engine_risk_count} risk{analysis.engine_risk_count !== 1 ? 's' : ''} — versioned baseline × multipliers
                        </div>
                      </div>

                    </div>

                    {/* Cost category breakdown */}
                    {(() => {
                      const COST_META = {
                        direct_costs: {
                          label: 'Direct Costs',
                          pct: 30,
                          icon: '💸',
                          color: { card: 'border-red-200 bg-red-50', badge: 'bg-red-100 text-red-700', accent: 'text-red-700', bar: 'bg-red-400' },
                          description: 'Immediate out-of-pocket expenses triggered directly by the incident. These are the first costs incurred and typically the easiest to quantify.',
                          examples: ['Incident response & digital forensics', 'Legal counsel and regulatory advice', 'Breach notification and credit-monitoring services', 'Emergency IT contractor hours'],
                          note: 'Allocated as 30% of total portfolio expected annual loss. Based on industry-average cost-structure models for cybersecurity incidents.',
                        },
                        business_interruption: {
                          label: 'Business Interruption',
                          pct: 25,
                          icon: '⏸️',
                          color: { card: 'border-orange-200 bg-orange-50', badge: 'bg-orange-100 text-orange-700', accent: 'text-orange-700', bar: 'bg-orange-400' },
                          description: 'Revenue lost and productivity costs while systems are unavailable or degraded. Covers downtime windows, missed transactions, and SLA breaches.',
                          examples: ['Lost sales revenue during outage', 'Reduced staff throughput', 'SLA and contract penalty payments', 'Emergency overtime and surge staffing'],
                          note: 'Allocated as 25% of total portfolio expected annual loss. Ransomware (RW) and Operational Outage (OO) risk types are the primary drivers.',
                        },
                        recovery_costs: {
                          label: 'Recovery & Remediation',
                          pct: 20,
                          icon: '🔧',
                          color: { card: 'border-blue-200 bg-blue-50', badge: 'bg-blue-100 text-blue-700', accent: 'text-blue-700', bar: 'bg-blue-400' },
                          description: 'Costs to restore systems, close vulnerabilities, and harden controls after an incident. Includes both one-time restoration and ongoing security improvements.',
                          examples: ['System rebuild and data restoration', 'Security tooling and platform upgrades', 'Vulnerability remediation sprints', 'Post-incident staff awareness training'],
                          note: 'Allocated as 20% of total portfolio expected annual loss. Higher for organisations with a large number of technical findings.',
                        },
                        regulatory_fines: {
                          label: 'Regulatory Fines & Penalties',
                          pct: 15,
                          icon: '⚖️',
                          color: { card: 'border-purple-200 bg-purple-50', badge: 'bg-purple-100 text-purple-700', accent: 'text-purple-700', bar: 'bg-purple-400' },
                          description: 'Potential penalties and compliance costs imposed by regulators. Weighted by the organisation\'s regulatory multiplier (e.g. Healthcare 1.8×, Finance 1.7×).',
                          examples: ['GDPR / CCPA civil monetary penalties', 'HIPAA enforcement actions', 'PCI-DSS non-compliance assessments', 'SEC or FCA enforcement fines'],
                          note: 'Allocated as 15% of total portfolio expected annual loss. This figure already reflects your industry\'s regulatory multiplier applied during SLE calculation.',
                        },
                        reputation_damage: {
                          label: 'Reputation & Brand Damage',
                          pct: 10,
                          icon: '📉',
                          color: { card: 'border-pink-200 bg-pink-50', badge: 'bg-pink-100 text-pink-700', accent: 'text-pink-700', bar: 'bg-pink-400' },
                          description: 'Long-term revenue impact from customer churn, diminished brand equity, and loss of stakeholder trust following a public breach or outage.',
                          examples: ['Customer attrition over 12–24 months', 'Reduced pipeline and deal win rates', 'Crisis PR and communications spend', 'Executive and board-level management time'],
                          note: 'Allocated as 10% of total portfolio expected annual loss. Data Breach (DB) and BEC findings are the primary drivers of reputational risk.',
                        },
                      };

                      const hasBands = analysis.has_scenario_bands;

                      return (
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3">
                            Cost Category Breakdown
                            <span className="ml-2 text-xs font-normal text-gray-400">— click a category for details</span>
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {Object.entries(analysis.breakdown).map(([key, amount]) => {
                              const meta = COST_META[key];
                              const amtLow = analysis.breakdown_low?.[key] ?? amount;
                              const amtHigh = analysis.breakdown_high?.[key] ?? amount;
                              const isActive = activeCostCategory === key;
                              return (
                                <button
                                  key={key}
                                  onClick={() => setActiveCostCategory(isActive ? null : key)}
                                  className={`text-left rounded-lg border p-4 transition-all focus:outline-none ${meta.color.card} ${isActive ? 'ring-2 ring-offset-1 ring-gray-400 shadow-md' : 'hover:shadow-sm'}`}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                                      <span>{meta.icon}</span>
                                      {meta.label}
                                    </span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${meta.color.badge}`}>
                                      {meta.pct}%
                                    </span>
                                  </div>
                                  {hasBands ? (
                                    <>
                                      <div className={`text-lg font-bold ${meta.color.accent}`}>
                                        {formatImpact(amtLow)} – {formatImpact(amtHigh)}+
                                      </div>
                                      <div className="text-xs text-gray-500 mt-0.5">
                                        Most Likely: ${Math.round(amount).toLocaleString()}
                                      </div>
                                    </>
                                  ) : (
                                    <div className={`text-xl font-bold ${meta.color.accent}`}>
                                      ${Math.round(amount).toLocaleString()}
                                    </div>
                                  )}
                                  <div className="text-xs text-gray-400 mt-1.5">
                                    {isActive ? '▲ Hide detail' : '▼ Show detail'}
                                  </div>
                                </button>
                              );
                            })}
                          </div>

                          {/* Expanded detail panel — shown below grid when a category is active */}
                          {activeCostCategory && COST_META[activeCostCategory] && (() => {
                            const key = activeCostCategory;
                            const meta = COST_META[key];
                            const amt = analysis.breakdown[key] ?? 0;
                            const amtLow = analysis.breakdown_low?.[key] ?? amt;
                            const amtHigh = analysis.breakdown_high?.[key] ?? amt;
                            const totalEAL = analysis.engine_calculated_impact || 1;

                            // Which risk types drive this category most (from engine breakdown)
                            const riskTypeContributors = analysis.engine_by_risk_type
                              ? Object.entries(analysis.engine_by_risk_type)
                                .map(([code, d]) => ({
                                  code,
                                  name: { DB: 'Data Breach', RW: 'Ransomware', RA: 'Unauthorized Access', BEC: 'Business Email Compromise', OO: 'Operational Outage', IP: 'Intellectual Property', SC: 'Supply Chain', DDOS: 'DDoS', FF: 'Financial Fraud', IT: 'Insider Threat', CM: 'Cloud Misconfiguration', TPE: 'Third-Party Exposure', AI: 'AI System Risk' }[code] || code,
                                  contribution: (d.subtotal * meta.pct / 100),
                                  contributionLow: (d.subtotalLow * meta.pct / 100),
                                  contributionHigh: (d.subtotalHigh * meta.pct / 100),
                                  pctOfCategory: totalEAL > 0 ? ((d.subtotal / totalEAL) * 100).toFixed(1) : '0.0',
                                }))
                                .sort((a, b) => b.contribution - a.contribution)
                              : [];

                            return (
                              <div className={`mt-3 rounded-lg border-2 p-5 ${meta.color.card} ${meta.color.card.replace('bg-', 'border-')}`}>
                                {/* Header */}
                                <div className="flex items-start justify-between mb-4">
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-2xl">{meta.icon}</span>
                                      <h5 className="text-base font-bold text-gray-800">{meta.label}</h5>
                                      <span className={`text-xs px-2 py-0.5 rounded font-mono ${meta.color.badge}`}>{meta.pct}% of EAL</span>
                                    </div>
                                    <p className="text-sm text-gray-600 max-w-2xl">{meta.description}</p>
                                  </div>
                                  <button onClick={() => setActiveCostCategory(null)} className="text-gray-400 hover:text-gray-600 text-lg ml-4 flex-shrink-0">✕</button>
                                </div>

                                {/* Amount hero */}
                                <div className="grid grid-cols-3 gap-3 mb-4">
                                  {hasBands && (
                                    <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
                                      <div className="text-base font-bold text-blue-600">${Math.round(amtLow).toLocaleString()}</div>
                                      <div className="text-xs text-gray-400 mt-0.5">Low Scenario</div>
                                    </div>
                                  )}
                                  <div className={`bg-white rounded-lg p-3 text-center border-2 ${hasBands ? '' : 'col-span-3'}`} style={hasBands ? {} : {}}>
                                    <div className={`text-base font-bold ${meta.color.accent}`}>${Math.round(amt).toLocaleString()}</div>
                                    <div className="text-xs text-gray-400 mt-0.5">{hasBands ? 'Most Likely' : 'Expected'}</div>
                                  </div>
                                  {hasBands && (
                                    <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
                                      <div className="text-base font-bold text-orange-600">${Math.round(amtHigh).toLocaleString()}</div>
                                      <div className="text-xs text-gray-400 mt-0.5">High Scenario</div>
                                    </div>
                                  )}
                                </div>

                                {/* Allocation formula */}
                                <div className="bg-white rounded-lg p-3 mb-4 border border-gray-100 text-xs text-gray-600">
                                  <span className="font-semibold text-gray-700">Allocation formula: </span>
                                  {meta.pct}% × Total Portfolio EAL (${Math.round(analysis.total_potential_impact).toLocaleString()})
                                  {hasBands && ` · Range: ${meta.pct}% × [${formatImpact(analysis.total_potential_impact_low)} – ${formatImpact(analysis.total_potential_impact_high)}+]`}
                                </div>

                                {/* What's included */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">What's included</div>
                                    <ul className="space-y-1">
                                      {meta.examples.map((ex, i) => (
                                        <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                                          <span className={`mt-0.5 text-base leading-none ${meta.color.accent}`}>·</span>
                                          {ex}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>

                                  {/* Risk type drivers */}
                                  {riskTypeContributors.length > 0 && (
                                    <div>
                                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Top risk-type drivers</div>
                                      <div className="space-y-1.5">
                                        {riskTypeContributors.slice(0, 5).map(({ code, name, contribution, contributionLow, contributionHigh, pctOfCategory }) => (
                                          <div key={code}>
                                            <div className="flex items-center justify-between text-xs mb-0.5">
                                              <span className="flex items-center gap-1">
                                                <span className={`px-1 py-0.5 rounded text-xs font-mono ${meta.color.badge}`}>{code}</span>
                                                <span className="text-gray-600 truncate max-w-[100px]">{name}</span>
                                              </span>
                                              <span className="font-semibold text-gray-700 flex-shrink-0">
                                                {hasBands
                                                  ? `${formatImpact(contributionLow)} – ${formatImpact(contributionHigh)}+`
                                                  : `$${Math.round(contribution).toLocaleString()}`
                                                }
                                              </span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-1">
                                              <div className={`h-1 rounded-full ${meta.color.bar}`} style={{ width: `${pctOfCategory}%` }} />
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Methodology note */}
                                <div className="text-xs text-gray-500 italic border-t border-gray-200 pt-3">
                                  📌 {meta.note}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}

                    {/* Engine Total Aggregation — expandable */}
                    {analysis.engine_by_risk_type && Object.keys(analysis.engine_by_risk_type).length > 0 && (() => {
                      const riskTypeNames = {
                        DB: 'Data Breach',
                        RW: 'Ransomware',
                        RA: 'Unauthorized Access',
                        FF: 'Financial Fraud',
                        OO: 'Operational Outage',
                        SC: 'Supply Chain',
                        IP: 'Intellectual Property Theft',
                        BEC: 'Business Email Compromise',
                        IT: 'Insider Threat',
                        CM: 'Cloud Misconfiguration',
                        DDOS: 'DDoS Attack',
                        TPE: 'Third-Party Exposure',
                        AI: 'AI System Risk',
                      };
                      const rows = Object.entries(analysis.engine_by_risk_type)
                        .sort((a, b) => b[1].subtotal - a[1].subtotal);
                      return (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                            onClick={() => setShowEngineBreakdown(v => !v)}
                          >
                            <span className="font-semibold text-gray-800">
                              📊 Engine Total Aggregation
                              <span className="ml-2 text-sm font-normal text-gray-500">
                                ({analysis.engine_risk_count} risk{analysis.engine_risk_count !== 1 ? 's' : ''})
                              </span>
                            </span>
                            <span className="text-gray-500 text-lg">{showEngineBreakdown ? '▲' : '▼'}</span>
                          </button>
                          {showEngineBreakdown && (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
                                    <th className="text-left px-4 py-2">Risk Type</th>
                                    <th className="text-center px-4 py-2">Count</th>
                                    <th className="text-center px-4 py-2">%</th>
                                    <th className="text-right px-4 py-2">Most Likely (avg)</th>
                                    {analysis.has_scenario_bands
                                      ? <th className="text-right px-4 py-2">Range (Low – High)</th>
                                      : <th className="text-right px-4 py-2">Subtotal</th>
                                    }
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map(([code, { count, subtotal, subtotalLow, subtotalHigh }], i) => {
                                    const pct = analysis.engine_calculated_impact > 0
                                      ? ((subtotal / analysis.engine_calculated_impact) * 100).toFixed(1)
                                      : '0.0';
                                    return (
                                      <tr key={code} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-4 py-2 font-medium text-gray-800">
                                          <span className="inline-block mr-2 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-xs rounded font-mono">
                                            {code}
                                          </span>
                                          {riskTypeNames[code] || code}
                                        </td>
                                        <td className="px-4 py-2 text-center text-gray-700">{count}</td>
                                        <td className="px-4 py-2 text-center">
                                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                            {pct}%
                                          </span>
                                        </td>
                                        <td className="px-4 py-2 text-right text-gray-700">
                                          ${Math.round(subtotal / count).toLocaleString()}
                                        </td>
                                        {analysis.has_scenario_bands ? (
                                          <td className="px-4 py-2 text-right font-semibold text-gray-900">
                                            <span className="text-blue-600">{formatImpact(subtotalLow)}</span>
                                            <span className="text-gray-400 mx-1">–</span>
                                            <span className="text-orange-600">{formatImpact(subtotalHigh)}+</span>
                                            <div className="text-xs text-gray-400 font-normal">
                                              ML: {formatImpact(subtotal)}
                                            </div>
                                          </td>
                                        ) : (
                                          <td className="px-4 py-2 text-right font-semibold text-gray-900">
                                            ${Math.round(subtotal).toLocaleString()}
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                <tfoot>
                                  <tr className="bg-emerald-50 border-t-2 border-emerald-200 font-bold">
                                    <td className="px-4 py-2 text-gray-800">Total</td>
                                    <td className="px-4 py-2 text-center text-gray-800">{analysis.engine_risk_count}</td>
                                    <td className="px-4 py-2 text-center text-gray-500 text-xs font-normal">100%</td>
                                    <td className="px-4 py-2 text-right text-gray-500 text-xs font-normal">—</td>
                                    {analysis.has_scenario_bands ? (
                                      <td className="px-4 py-2 text-right text-emerald-700">
                                        <span className="text-blue-600">{formatImpact(analysis.engine_calculated_impact_low)}</span>
                                        <span className="text-gray-400 mx-1">–</span>
                                        <span className="text-orange-600">{formatImpact(analysis.engine_calculated_impact_high)}+</span>
                                        <div className="text-xs text-emerald-600 font-normal">
                                          ML: ${Math.round(analysis.engine_calculated_impact).toLocaleString()}
                                        </div>
                                      </td>
                                    ) : (
                                      <td className="px-4 py-2 text-right text-emerald-700">
                                        ${Math.round(analysis.engine_calculated_impact).toLocaleString()}
                                      </td>
                                    )}
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Recommendations */}
                    <div className="bg-purple-50 p-6 rounded-lg">
                      <h4 className="font-medium mb-3">Recommendations</h4>
                      <ul className="list-disc list-inside text-purple-800 space-y-1">
                        {analysis.recommendations.map((rec, i) => (
                          <li key={`rec-${i}`}>{rec}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <Button
                        variant="secondary"
                        onClick={() => setStep("setup")}
                      >
                        Back to Setup
                      </Button>
                      <Button
                        onClick={() => setShowMonetaryAnalysis(false)}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                );
              }

              return null;
            })()}
          </div>
        )}


        {showAssignModal && selectedRisk && (
          <AssignRiskModal
            show={showAssignModal}
            onClose={() => setShowAssignModal(false)}
            risk={selectedRisk}
            refreshData={fetchAssessmentRisks}
            riskId={selectedRisk?.id}
            riskType="AI"
            dealId={assessment?.DealId || assessment?.dealID || assessment?.dealId}
          />
        )}
        {showBulkAssignModal && (
          <BulkAssignRiskModal
            show={showBulkAssignModal}
            onClose={() => { setShowBulkAssignModal(false); setSelectedRiskIds(new Set()); }}
            risks={risks.filter(r => selectedRiskIds.has(r.id))}
            riskType="AI"
            refreshData={fetchAssessmentRisks}
            dealId={assessment?.DealId || assessment?.dealID || assessment?.dealId}
          />
        )}

      </div>
    </div >
  );
};



// Risk Category Bar Chart Component
const getCategoryBarColor = (score) => {
  if (score >= 75) return "#b91c1c";   // Red – Critical
  if (score >= 50) return "#f97316";   // Orange – High
  if (score >= 25) return "#eab308";   // Yellow – Medium
  if (score >= 10) return "#999999";
  return "#7f8080";                    // Green – Low
};

const RiskCategoryBarChart = ({ riskData, activeDomain, onDomainClick }) => {
  const chartData = Object.entries(riskData || {}).map(([name, value]) => ({
    name,
    score: Number(value) || 0
  }));

  if (!chartData.length) {
    return (
      <Card>
        <h3 className="text-lg font-semibold mb-1">Risk Exposure by Category</h3>
        <div className="text-gray-500 text-sm text-center py-10">
          No risk category data available
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-semibold">Risk Exposure by Category</h3>
        {activeDomain && (
          <button
            onClick={() => onDomainClick?.(activeDomain)}
            className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
          >
            Filtering: {activeDomain} <span className="text-indigo-400">✕</span>
          </button>
        )}
      </div>
      <p className="text-xs text-gray-400 mb-3">Click a bar to filter risks by category</p>
      <div style={{ width: "100%", height: 250, maxHeight: 250 }}>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              tickFormatter={(value) => value.length > 16 ? value.substring(0, 16) + '…' : value}
              tick={{ fontSize: 13 }}
              angle={-35}
              textAnchor="end"
              interval={0}
              height={120}
            />
            <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} interval={0} />
            <Tooltip />
            <Bar
              dataKey="score"
              radius={[6, 6, 0, 0]}
              onClick={(data) => onDomainClick?.(data.name)}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={activeDomain === entry.name ? '#6366f1' : getCategoryBarColor(entry.score)}
                  opacity={activeDomain && activeDomain !== entry.name ? 0.45 : 1}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export default AssessmentRisksView;
